import React, { useRef, useCallback } from "react";
import { AddCommaNumber } from "../../helpers/Utils";
import IntlMessages from "../../helpers/IntlMessages";
import ProductImage from "../../components/common/ProductImage";

const useDragScroll = () => {
  const ref = useRef(null);
  const dragState = useRef({ isDown: false, startX: 0, scrollLeft: 0, moved: false });

  const onMouseDown = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    dragState.current = {
      isDown: true,
      startX: e.pageX - el.offsetLeft,
      scrollLeft: el.scrollLeft,
      moved: false,
    };
    el.style.cursor = "grabbing";
  }, []);

  const onMouseMove = useCallback((e) => {
    const ds = dragState.current;
    if (!ds.isDown) return;
    e.preventDefault();
    const el = ref.current;
    if (!el) return;
    const x = e.pageX - el.offsetLeft;
    const walk = x - ds.startX;
    if (Math.abs(walk) > 3) ds.moved = true;
    el.scrollLeft = ds.scrollLeft - walk;
  }, []);

  const onMouseUp = useCallback(() => {
    dragState.current.isDown = false;
    if (ref.current) ref.current.style.cursor = "grab";
  }, []);

  const onMouseLeave = useCallback(() => {
    dragState.current.isDown = false;
    if (ref.current) ref.current.style.cursor = "grab";
  }, []);

  return { ref, dragState, onMouseDown, onMouseMove, onMouseUp, onMouseLeave };
};

const SuggestionPanel = ({ suggestions, onAddProduct }) => {
  const { frequently_bought = [], cross_category = [] } = suggestions || {};
  const { ref, dragState, onMouseDown, onMouseMove, onMouseUp, onMouseLeave } = useDragScroll();

  const allSuggestions = [...frequently_bought, ...cross_category];
  if (!allSuggestions.length) return null;

  return (
    <div
      className="suggestion-panel mx-2 mb-2 p-2"
      style={{
        background: "linear-gradient(135deg, #f0f7ff 0%, #fff8e1 100%)",
        border: "1px solid #e3e8ef",
        borderRadius: "10px",
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      <div
        className="d-flex align-items-center mb-1"
        style={{ fontSize: "11px", color: "#666", fontWeight: 600 }}
      >
        <i className="simple-icon-bulb mr-1" style={{ color: "#ff9800" }} />
        <IntlMessages id="suggestion.title" />
      </div>
      <div
        ref={ref}
        className="d-flex"
        style={{
          gap: "6px",
          overflowX: "auto",
          paddingBottom: "2px",
          cursor: "grab",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
      >
        {allSuggestions.map((product) => (
          <SuggestionCard
            key={product.id}
            product={product}
            onAdd={onAddProduct}
            dragState={dragState}
          />
        ))}
      </div>
    </div>
  );
};

const SuggestionCard = ({ product, onAdd, dragState }) => {
  const price = product.active_sale && parseFloat(product.price_sale) > 0
    ? parseFloat(product.price_sale)
    : parseFloat(product.price) || 0;

  return (
    <div
      className="d-flex align-items-center cursor-pointer"
      style={{
        background: "#fff",
        border: "1px solid #e8e8e8",
        padding: "4px 8px",
        minWidth: "140px",
        maxWidth: "180px",
        borderRadius: "10px",
        flex: "0 0 auto",
        userSelect: "none",
      }}
      onClick={() => {
        if (dragState?.current?.moved) return;
        if (onAdd) {
          // Build a minimal product object compatible with onClickImage
          onAdd({
            id: product.id,
            name: product.name,
            price: product.price,
            price_sale: product.price_sale,
            active_sale: product.active_sale,
            image: product.image,
            category: product.category || { id: product.category_id },
            size: [],
            topping: [],
            sizeSelectedId: [],
            flavorSelectedId: [],
            toppingSelectedId: [],
            variants: [],
            selectedAttributes: {},
          });
        }
      }}
    >
      <ProductImage
        image={product.image}
        alt={product.name}
        fnbType={product.fnb_type}
        iconSize="18px"
        style={{
          width: 36,
          height: 36,
          borderRadius: 6,
          objectFit: "cover",
          marginRight: 6,
          flexShrink: 0,
        }}
      />
      <div style={{ overflow: "hidden", flex: 1 }}>
        <div
          style={{
            fontSize: "11px",
            fontWeight: 600,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            lineHeight: "14px",
          }}
        >
          {product.name}
        </div>
        <div style={{ fontSize: "10px", color: "#e65100", fontWeight: 600 }}>
          {AddCommaNumber(price)}đ
        </div>
        <div style={{ fontSize: "9px", color: "#999" }}>
          {product.suggestion_type === "frequently_bought" ? (
            <IntlMessages id="suggestion.frequently-bought" />
          ) : (
            <IntlMessages id="suggestion.bestseller" />
          )}
        </div>
      </div>
      <div
        className="ml-1 d-flex align-items-center justify-content-center"
        style={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          background: "#145388",
          color: "#fff",
          fontSize: 14,
          flexShrink: 0,
          cursor: "pointer",
        }}
      >
        +
      </div>
    </div>
  );
};

export default SuggestionPanel;
